<script setup>
import { onBeforeUnmount, onMounted, ref, watch } from "vue";
import Quill from "quill";

const props = defineProps({
  modelValue: {
    type: String,
    default: "",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue", "change"]);

const editorRef = ref(null);
let quill = null;
let syncingFromProps = false;

const EMPTY_HTML = "<p><br></p>";

const getNormalizedHtml = (html) => {
  if (!html || html === EMPTY_HTML) {
    return "";
  }
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, "text/html");
  const wrapper = doc.body.firstElementChild;
  if (!wrapper) {
    return "";
  }

  wrapper.querySelectorAll("span.ql-ui").forEach((node) => node.remove());

  Array.from(wrapper.querySelectorAll("ol")).forEach((listNode) => {
    const childItems = Array.from(listNode.children).filter(
      (child) => child.tagName?.toLowerCase() === "li",
    );
    if (childItems.length === 0) return;

    const itemTypes = childItems.map(
      (item) => item.getAttribute("data-list") || "ordered",
    );
    const uniqueTypes = Array.from(new Set(itemTypes));

    if (uniqueTypes.length === 1) {
      const nextTag = uniqueTypes[0] === "bullet" ? "ul" : "ol";
      const replacement = doc.createElement(nextTag);
      childItems.forEach((item) => {
        item.removeAttribute("data-list");
        replacement.appendChild(item.cloneNode(true));
      });
      listNode.replaceWith(replacement);
      return;
    }

    const fragment = doc.createDocumentFragment();
    let currentList = null;
    let currentType = null;

    childItems.forEach((item) => {
      const itemType = item.getAttribute("data-list") || "ordered";
      const tagName = itemType === "bullet" ? "ul" : "ol";
      if (!currentList || currentType !== tagName) {
        currentList = doc.createElement(tagName);
        currentType = tagName;
        fragment.appendChild(currentList);
      }
      item.removeAttribute("data-list");
      currentList.appendChild(item.cloneNode(true));
    });

    listNode.replaceWith(fragment);
  });

  return wrapper.innerHTML;
};

const syncEditorFromProps = (html) => {
  if (!quill) return;
  const nextHtml = html || EMPTY_HTML;
  if (quill.root.innerHTML === nextHtml) return;

  syncingFromProps = true;
  const shouldRestoreSelection = quill.hasFocus();
  const selection = shouldRestoreSelection ? quill.getSelection() : null;
  const delta = quill.clipboard.convert(nextHtml);
  quill.setContents(delta, "silent");
  if (selection) {
    quill.setSelection(selection, "silent");
  }
  syncingFromProps = false;
};

const emitContent = () => {
  if (!quill || syncingFromProps) return;
  const html = getNormalizedHtml(quill.root.innerHTML);
  const text = quill.getText().trim();
  emit("update:modelValue", html);
  emit("change", { html, text });
};

onMounted(() => {
  quill = new Quill(editorRef.value, {
    theme: "snow",
    modules: {
      toolbar: false,
      clipboard: {
        matchVisual: false,
      },
    },
    formats: ["bold", "list"],
    readOnly: props.disabled,
  });

  syncEditorFromProps(props.modelValue);
  quill.on("text-change", emitContent);
  quill.enable(!props.disabled);
});

watch(
  () => props.modelValue,
  (value) => {
    syncEditorFromProps(value);
  },
);

watch(
  () => props.disabled,
  (value) => {
    if (quill) {
      quill.enable(!value);
    }
  },
);

onBeforeUnmount(() => {
  if (quill) {
    quill.off("text-change", emitContent);
    quill = null;
  }
});
</script>

<template>
  <div class="scope-rich-text-editor" :class="{ disabled }">
    <div ref="editorRef" class="scope-rich-text-surface"></div>
  </div>
</template>

<style scoped>
.scope-rich-text-editor {
  width: 100%;
}

.scope-rich-text-surface :deep(.ql-toolbar) {
  display: none;
}

.scope-rich-text-surface :deep(.ql-container) {
  border: 1px solid #d2d6da;
  border-radius: 0.5rem;
  font-size: 12px !important;
  background: #fff;
}

.scope-rich-text-editor.disabled .scope-rich-text-surface :deep(.ql-container) {
  border-radius: 0.5rem;
  background: #f8f9fa;
}

.scope-rich-text-surface :deep(.ql-editor) {
  min-height: 42px;
  height: auto;
  overflow-y: hidden;
  padding: 0.5rem 0.75rem;
  font-size: 12px !important;
  line-height: 1.4;
  white-space: normal;
}

.scope-rich-text-surface :deep(.ql-editor *),
.scope-rich-text-surface :deep(.ql-editor p),
.scope-rich-text-surface :deep(.ql-editor ol),
.scope-rich-text-surface :deep(.ql-editor ul),
.scope-rich-text-surface :deep(.ql-editor li),
.scope-rich-text-surface :deep(.ql-editor strong),
.scope-rich-text-surface :deep(.ql-editor em),
.scope-rich-text-surface :deep(.ql-editor u) {
  font-size: 12px !important;
}

.scope-rich-text-surface :deep(.ql-editor p),
.scope-rich-text-surface :deep(.ql-editor ol),
.scope-rich-text-surface :deep(.ql-editor ul) {
  margin-bottom: 0.25rem;
}

.scope-rich-text-surface :deep(.ql-editor p:last-child),
.scope-rich-text-surface :deep(.ql-editor ol:last-child),
.scope-rich-text-surface :deep(.ql-editor ul:last-child) {
  margin-bottom: 0;
}
</style>
